import { Suspense, useEffect, useState, Fragment } from 'react';
import {
  getGuidebookSlugsFromIds,
  search,
  titleCaseAndUnSlugify,
} from '../../utils/utils';
import { Link, useSearchParams } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import Filters from './Filters';
import Loading from '../Loading';
import InnerContent from '../InnerContent';
import { decode } from 'html-entities';

let Search = () => {
  let [searchParams, setSearchParams] = useSearchParams();
  // Set our initial search values from the searchParams
  let initialPage = searchParams.get('searchPage');
  let initialSearchValue = searchParams.get('searchValue');
  let initialFilters = searchParams.get('searchFilters');
  let initialPostType = searchParams.get('postType');
  let initialSortBy = searchParams.get('sortBy');
  let instantSearch = searchParams.get('instantSearch') ? false : true;

  let [searchValue, setSearchValue] = useState(initialSearchValue || '');
  let [filters, setFilters] = useState(
    initialFilters ? initialFilters.split(',') : []
  );
  let [results, setResults] = useState([]);
  let [searchPage, setPage] = useState(initialPage);
  let [totalPages, setTotalPages] = useState(null);
  let [postType, setPostType] = useState(
    initialPostType ? initialPostType.split(',') : ['post', 'resource']
  );
  let [sortBy, setSortBy] = useState(initialSortBy || 'relevance');

  let [loading, setLoading] = useState(false);
  let [firstLoad, setFirstLoad] = useState(instantSearch);

  let handleSearch = async () => {
    if (firstLoad) {
      setFirstLoad(false);
    }

    setLoading(true);
    let searchObject = {
      searchValue,
      // If what is in the current searchParams is not the same as what is in the searchValue state, then we need to reset the page to 1.
      searchPage:
        searchParams.get('searchValue') !== searchValue ? 1 : searchPage,
      searchFilters: filters.join(','),
      perPage: 12,
      postType: postType.join(','),
      sortBy,
    };

    let results = await search(searchObject);

    setPage(searchObject.searchPage);
    setSearchParams(searchObject);
    setTotalPages(parseInt(results.totalPages) || 0);
    setResults(results.data);
    setLoading(false);
  };

  useEffect(() => {
    if (firstLoad) return;
    if (searchParams.get('searchPage')) {
      setPage(searchParams.get('searchPage'));
    }
    handleSearch();
  }, [searchParams]);

  // When filters change, re-search.
  useEffect(() => {
    if (firstLoad) return;
    setSearchParams((prevSearchParams) => ({
      ...prevSearchParams,
      searchPage: 1,
    }));
  }, [filters]);

  useEffect(() => {
    if (firstLoad) return;
    // When sortBy changes, re-search.
    if (results.length > 0) {
      handleSearch();
    }
  }, [sortBy]);

  return (
    <div>
      <h2 id="shelter-search-header">Search the Library</h2>
      <InnerContent id={'shelter-library-viewer-inner-content-three'} />
      <SearchBar
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSearch={handleSearch}
        setSearchParams={setSearchParams}
      />

      <Filters
        filters={filters}
        setFilters={setFilters}
        postType={postType}
        setPostType={setPostType}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />
      {loading ? <Loading /> : <Results results={results} sortBy={sortBy} />}
      {results.length > 0 && (
        <Pagination
          setPage={setPage}
          searchPage={searchPage}
          setSearchParams={setSearchParams}
          searchParams={Object.fromEntries(searchParams.entries())}
          totalPages={totalPages}
          page={searchPage}
        />
      )}
    </div>
  );
};

let SearchBar = ({
  searchValue,
  setSearchValue,
  handleSearch,
  setSearchParams,
}) => {
  onKeyPress = (e) => {
    if (e.which === 13) {
      // setSearchParams((prevSearchParams) => ({
      //   ...prevSearchParams,
      //   searchPage: 1,
      // }));
      handleSearch();
    }
  };
  return (
    <div className="search-main flex items-center space-x-4">
      <input
        id="slv-search"
        type="text"
        className="mb-0"
        value={searchValue}
        onKeyDown={onKeyPress}
        onChange={(e) => setSearchValue(e.target.value)}
      />
      <button
        className="search-button btn uw-button uw-button-red"
        onClick={() => {
          // setSearchParams((prevSearchParams) => ({
          //   ...prevSearchParams,
          //   searchPage: 1,
          // }));
          handleSearch();
        }}
      >
        <span className="whitespace-nowrap">Search</span>
      </button>
    </div>
  );
};

let Results = ({ results, sortBy }) => {
  let [guidebookSlugs, setGuidebookSlugs] = useState(null);

  useEffect(() => {
    // If we have results construct an id to slug map
    if (results && results?.length > 0) {
      let guidebookIds = [];
      results?.map((result) => {
        if (result.type === 'post') {
          guidebookIds.push(...result.guidebook);
        }
      });
      async function fetch() {
        let results = await getGuidebookSlugsFromIds(guidebookIds);
        setGuidebookSlugs(results);
      }
      fetch();
    }
  }, [results]);

  return (
    <div className="flex flex-col">
      {results.length === 0 && <p> No results </p>}
      {results &&
        guidebookSlugs &&
        results.map((result) => {
          return (
            <ResultCard
              key={result.id}
              result={result}
              guidebookSlugs={guidebookSlugs}
              sortBy={sortBy}
            />
          );
        })}
    </div>
  );
};

let ResultCard = ({ result, guidebookSlugs, sortBy }) => {
  let date = new Date(result.modified);
  let dateString = date.toLocaleDateString();
  let resultType = result.type === 'post' ? 'Guidebook Chapter' : 'Resource';

  let publishedDate = new Date(result.date);
  let publishedDateString = publishedDate.toLocaleDateString();

  let getResultLink = () => {
    let guidebookSlug = '';
    let belongsToGuidebook = result.guidebook[0];

    // search guidebook slugs for the guidebook that matches the result's guidebook id
    guidebookSlugs.map((guidebook) => {
      if (guidebook.id === belongsToGuidebook) {
        guidebookSlug = guidebook.slug;
      }
    });

    return `/library/guidebooks/${guidebookSlug}/${result.slug}`;
  };

  let getGuidebook = () => {
    if (result.type === 'post') {
      let guideBook = {};
      let belongsToGuidebook = result.guidebook[0];

      guidebookSlugs.map((guidebook) => {
        if (guidebook.id === belongsToGuidebook) {
          console.log(guidebook);
          guideBook = guidebook;
        }
      });

      console.log(guideBook);

      return guideBook;
    }
  };

  let link =
    result.type === 'post'
      ? getResultLink()
      : `/library/resources/${result.slug}`;

  let guidebook = getGuidebook();

  return (
    <div className="resource">
      <div className="mt-8">
        <h4>
          <Link reloadDocument to={link}>
            <span
              dangerouslySetInnerHTML={{ __html: result.title.rendered }}
            ></span>
          </Link>
        </h4>
        <div className="flex flex-col text-gray-500">
          <div className="flex space-x-1">
            <div>Updated: {dateString}</div>
            <div>|</div>
            <div>{guidebook && decode(guidebook.name)}</div>
            <div>{resultType}</div>
          </div>

          <div className="text-sm">
            {result.category_nicenames &&
              result.category_nicenames.length > 0 &&
              result.category_nicenames.map((category, index) => {
                return (
                  <Fragment key={index}>
                    {category}
                    {index + 1 !== result.category_nicenames.length && ', '}
                  </Fragment>
                );
              })}
          </div>
        </div>
        <div
          dangerouslySetInnerHTML={{ __html: result.excerpt.rendered }}
        ></div>
      </div>
      {publishedDateString && (
        <>
          <div className="text-gray-500 text-xs">
            Published: {publishedDateString}
          </div>
        </>
      )}
    </div>
  );
};

let Pagination = ({
  setPage,
  totalPages,
  setSearchParams,
  searchParams,
  page,
}) => {
  return (
    <ReactPaginate
      forcePage={page - 1}
      onClick={() => {
        window.scrollTo(0, 0);
      }}
      breakLabel="..."
      nextLabel="next >"
      className="flex items-center justify-between px-4 my-10 list-none border-t border-gray-200 sm:px-0"
      pageClassName="inline-flex items-center border-t-2 border-transparent pr-1  text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700"
      pageLinkClassName="inline-flex items-center px-4 pt-4"
      previousClassName="inline-flex items-center border-t-2 pt-4 border-transparent pr-1 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700"
      nextClassName="inline-flex items-center border-t-2 pt-4 border-transparent pr-1 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700"
      activeLinkClassName="inline-flex items-center border-t-2 border-gray-900 px-4 text-sm font-medium text-gray-900 font-bold"
      onPageChange={(event) => {
        setPage(event.selected + 1);
        setSearchParams({ ...searchParams, searchPage: event.selected + 1 });
      }}
      pageRangeDisplayed={3}
      pageCount={totalPages}
      previousLabel="< previous"
      renderOnZeroPageCount={null}
    />
  );
};

export default Search;
